import React, { useEffect, useState } from 'react';
import API, { API_URL } from '../api/api1';
import ProfilePhotoUpload from './ProfilePhotoUpload';
import { useToast } from '../context/ToastContext';
import '../styles/pages/profile.css';

const BACKEND = API_URL;
const makeUrl = (path) => {
  if (!path || typeof path !== 'string') return '';
  if (/^(blob:|data:|https?:)/i.test(path)) return path;
  if (path.startsWith('/api/')) return `${BACKEND}${path}`;
  if (path.startsWith('/uploads')) return `${BACKEND}/api${path}`;
  return `${BACKEND}${path.startsWith('/') ? path : `/${path}`}`;
};

// Roles a user can pick. `value` is what is sent to the API, `label` is what people see.
// If your backend uses a different value for admin, change it here.
const ROLE_OPTIONS = [
  { value: 'tenantowner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
];

const normalizeRole = (role) => {
  const key = String(role || '').toLowerCase().replace(/[\s_-]/g, '');
  if (key === 'tenantowner' || key === 'owner') return 'tenantowner';
  if (key === 'admin' || key === 'administrator' || key === 'tenantadmin') return 'admin';
  return key;
};

const roleLabel = (role) => ROLE_OPTIONS.find(o => o.value === normalizeRole(role))?.label || role || '';

const ICONS = {
  person: <><circle cx="12" cy="8" r="3" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
  mail: <><path d="M4 6h16v12H4z" /><path d="m4 7 8 6 8-6" /></>,
  building: <path d="M4 21V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v13M3 21h18M8 10h2m4 0h2m-8 4h2m4 0h2M4 6l8-3 8 3" />,
  crown: <><path d="M4 8l3.2 2.6L12 5l4.8 5.6L20 8l-1.4 9H5.4L4 8Z" /><path d="M6.5 20h11" /></>,
  shield: <><path d="M12 3 19 6v5c0 4.5-3 7.5-7 10-4-2.5-7-5.5-7-10V6l7-3Z" /><path d="m9 12 2 2 4-4" /></>,
  key: <><circle cx="8.2" cy="12" r="3.2" /><path d="M11.4 12H21m-3 0v3m-3-3v2.5" /></>,
  lock: <><rect x="4.5" y="10" width="15" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
  trash: <><path d="M4 7h16M9 7V5h6v2m-8 0 1 13h8l1-13" /><path d="M10 11v5m4-5v5" /></>,
};

const EMPTY_FORM = {
  fullName: '',
  email: '',
  phone: '',
  role: 'Administrator',
  organizationName: '',
  organizationPhone: '',
  organizationAddress: '',
  organizationLogo: '',
  organizationSignature: '',
  organizationUpiQr: '',
  organizationDomain: '',
  planType: 'Basic',
  ownerName: '',
  ownerEmail: '',
  lastLogin: '',
  status: '',
};

// Render the page instantly with whatever is already cached, then refresh it
// from the API. This removes the need for a loading skeleton.
const readCachedForm = () => {
  try {
    const cachedUser = JSON.parse(localStorage.getItem('userinfo') || '{}');
    const cachedTenant = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
    return {
      ...EMPTY_FORM,
      fullName: cachedUser.name || cachedUser.fullName || '',
      email: cachedUser.email || '',
      phone: cachedUser.phone || '',
      role: cachedUser.role || 'Administrator',
      organizationName: cachedTenant.tenant_name || cachedTenant.name || '',
      organizationPhone: cachedTenant.phone || '',
      organizationAddress: cachedTenant.address || '',
      organizationLogo: makeUrl(cachedTenant.logo_url || ''),
      organizationSignature: makeUrl(cachedTenant.signature_url || ''),
      organizationUpiQr: makeUrl(cachedTenant.upi_qr_code || ''),
      organizationDomain: cachedTenant.domain || '',
      planType: cachedTenant.plan_type || cachedTenant.plan || 'Basic',
      ownerName: cachedTenant.owner_name || '',
      ownerEmail: cachedTenant.owner_email || '',
      lastLogin: cachedUser.last_login || cachedUser.lastLogin || '',
      status: cachedUser.status || '',
    };
  } catch {
    return { ...EMPTY_FORM };
  }
};

const readCachedPhoto = () => {
  try {
    const cachedUser = JSON.parse(localStorage.getItem('userinfo') || '{}');
    return makeUrl(cachedUser.profile_photo || cachedUser.profilePhoto || cachedUser.photo_url || cachedUser.avatar_url || '');
  } catch {
    return '';
  }
};

const validateName = (value, label = 'Name') => {
  const text = String(value || '').trim();
  if (!text) return `${label} is required`;
  if (/\d/.test(text)) return `${label} should not contain numbers`;
  if (text.length < 2) return `${label} must be at least 2 characters`;
  return '';
};

const validateEmail = (value) => {
  const email = String(value || '').trim();
  if (!email) return 'Email address is required';
  if (!/^[^\s@]+@(?:[a-zA-Z0-9-]+\.)+com$/i.test(email)) {
    return 'Enter a valid email ending with .com (e.g. name@example.com)';
  }
  if (email.length > 150) return 'Email must not exceed 150 characters';
  return '';
};

const validatePhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length !== 10) return `Phone number must be exactly 10 digits (currently ${digits.length})`;
  if (!/^[6-9]\d{9}$/.test(digits)) return 'Phone number must start with 6, 7, 8, or 9';
  return '';
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const preventDigits = (event) => {
  if (/\d/.test(event.key)) event.preventDefault();
};

function SectionTitle({ icon, children, aside }) {
  return (
    <h3>
      <span className="profile-section-icon">
        <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS[icon]}</svg>
      </span>
      <span className="profile-section-label">{children}</span>
      {aside}
    </h3>
  );
}

function Detail({ label, value, fallback = 'Not provided', className = '' }) {
  return (
    <div className={`detail-item${className ? ` ${className}` : ''}`}>
      <dt>{label}</dt>
      <dd>{value || <span className="detail-empty">{fallback}</span>}</dd>
    </div>
  );
}

// Shows a value normally, and turns into an input (or a textarea) while editing.
function Field({
  id,
  label,
  value,
  display,
  editing,
  onChange,
  type = 'text',
  required = false,
  autoComplete,
  maxLength,
  placeholder,
  error,
  hint,
  multiline = false,
  className = '',
  inputMode,
  onKeyDown,
}) {
  if (!editing) return <Detail label={label} value={display ?? value} className={className} />;

  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className={`detail-item${className ? ` ${className}` : ''}${error ? ' has-error' : ''}`}>
      <dt>
        <label htmlFor={id}>
          {label}
          {required && <span className="field-required" aria-hidden="true">*</span>}
        </label>
      </dt>
      <dd>
        {multiline ? (
          <textarea
            id={id}
            className="detail-input"
            value={value ?? ''}
            required={required}
            maxLength={maxLength}
            placeholder={placeholder}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy}
            onChange={e => onChange(e.target.value)}
          />
        ) : (
          <input
            id={id}
            className="detail-input"
            type={type}
            value={value ?? ''}
            required={required}
            autoComplete={autoComplete}
            maxLength={maxLength}
            placeholder={placeholder}
            inputMode={inputMode}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy}
            onKeyDown={onKeyDown}
            onChange={e => onChange(e.target.value)}
          />
        )}
        {error
          ? <small className="detail-error" id={`${id}-error`}>{error}</small>
          : hint
            ? <small className="detail-hint" id={`${id}-hint`}>{hint}</small>
            : null}
      </dd>
    </div>
  );
}

function AssetTile({ id, label, src, emptyText, editing, onSelect, onRemove }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const showImage = Boolean(src) && !failed;
  return (
    <figure className="asset-tile">
      <figcaption>{label}</figcaption>
      <div className={`asset-frame${showImage ? '' : ' is-empty'}`}>
        {showImage
          ? <img src={src} alt={label} onError={() => setFailed(true)} />
          : (
            <span className="asset-empty">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3.5" y="5" width="17" height="14" rx="2.4" />
                <circle cx="9" cy="10" r="1.6" />
                <path d="m4.5 17 4.7-4.4 3.3 3 2.7-2.4 4.3 3.8" />
              </svg>
              {emptyText}
            </span>
          )}
      </div>
      {editing && (
        <div className="asset-actions">
          <input id={id} type="file" accept="image/*" className="visually-hidden" onChange={onSelect} />
          <label className="asset-btn" htmlFor={id}>{src ? 'Replace' : 'Upload'}</label>
          {src && <button type="button" className="asset-btn danger" onClick={onRemove}>Remove</button>}
        </div>
      )}
    </figure>
  );
}

function DialogIcon({ type = 'lock' }) {
  return (
    <span className={`profile-dialog-icon${type === 'danger' ? ' is-danger' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">{type === 'danger' ? ICONS.trash : ICONS.lock}</svg>
    </span>
  );
}

// Smaller avatar on tablets and phones so the identity card stays compact.
const getAvatarSize = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 112;
  if (window.matchMedia('(max-width: 600px)').matches) return 76;
  if (window.matchMedia('(max-width: 900px)').matches) return 92;
  return 112;
};

const useAvatarSize = () => {
  const [size, setSize] = useState(getAvatarSize);
  useEffect(() => {
    const onResize = () => setSize(getAvatarSize());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
};

export function ProfilePage() {
  const { showToast } = useToast();
  const avatarSize = useAvatarSize();
  const [user, setUser] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(readCachedForm);
  // The last saved version of the form and photo, so Cancel can restore them.
  const [savedForm, setSavedForm] = useState(readCachedForm);
  const [savedPhoto, setSavedPhoto] = useState(readCachedPhoto);
  const [profilePhoto, setProfilePhoto] = useState(readCachedPhoto);
  const [logoFile, setLogoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [pendingProfilePhoto, setPendingProfilePhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [confirmAsset, setConfirmAsset] = useState(null);
  const [removePhotoPending, setRemovePhotoPending] = useState(false);
  const [removeLogoPending, setRemoveLogoPending] = useState(false);
  const [removeSignaturePending, setRemoveSignaturePending] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => () => {
    if (pendingProfilePhoto?.previewUrl) URL.revokeObjectURL(pendingProfilePhoto.previewUrl);
  }, [pendingProfilePhoto]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await API.get('/api/users/me');
        const userData = response.data?.user || response.data?.data?.user || {};
        const tenantData = response.data?.tenant || response.data?.data?.tenant || {};
        const updatedForm = {
          ...EMPTY_FORM,
          fullName: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          role: userData.role || 'Administrator',
          organizationName: tenantData.tenant_name || tenantData.name || '',
          organizationPhone: tenantData.phone || '',
          organizationAddress: tenantData.address || '',
          organizationLogo: makeUrl(tenantData.logo_url || ''),
          organizationSignature: makeUrl(tenantData.signature_url || ''),
          organizationUpiQr: makeUrl(tenantData.upi_qr_code || ''),
          organizationDomain: tenantData.domain || '',
          planType: tenantData.plan_type || tenantData.plan || 'Basic',
          ownerName: tenantData.owner_name || '',
          ownerEmail: tenantData.owner_email || '',
          lastLogin: userData.last_login || userData.lastLogin || '',
          status: userData.status || '',
        };
        setUser({ ...userData, ...updatedForm, createdAt: userData.createdAt || userData.created_at });
        const photo = makeUrl(userData.profile_photo || userData.profilePhoto || userData.photo_url || userData.avatar_url || '');
        setProfilePhoto(photo);
        setSavedPhoto(photo);
        setForm(updatedForm);
        setSavedForm(updatedForm);
        localStorage.setItem('userinfo', JSON.stringify(userData));
        localStorage.setItem('tenantinfo', JSON.stringify(tenantData));
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        const fallback = readCachedForm();
        setUser(fallback);
        const photo = readCachedPhoto();
        setProfilePhoto(photo);
        setSavedPhoto(photo);
        setForm(fallback);
        setSavedForm(fallback);
      }
    };
    loadProfile();
  }, []);

  const normalizedRole = normalizeRole(user?.role || form.role);
  const isOwner = normalizedRole === 'tenantowner';
  const accountIsActive = form.status === 'active' || isOwner;
  const createdLabel = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
    : '';
  const lastLoginLabel = formatDateTime(form.lastLogin);
  const hasErrors = Object.values(formErrors).some(Boolean);

  const setField = (key) => (value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleNameChange = (key, label) => (value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setFormErrors(prev => ({ ...prev, [key]: validateName(value, label) }));
  };

  const handleEmailChange = (value) => {
    setForm(prev => ({ ...prev, email: value }));
    setFormErrors(prev => ({ ...prev, email: validateEmail(value) }));
  };

  const handlePhoneChange = (key) => (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
    setForm(prev => ({ ...prev, [key]: digits }));
    setFormErrors(prev => ({ ...prev, [key]: validatePhone(digits) }));
  };

  const startEditing = () => {
    if (!isOwner) {
      showToast('Only the tenant owner can edit profile settings.', 'error');
      return;
    }
    setFormErrors({});
    setEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isOwner) {
      showToast('Only the tenant owner can edit profile settings.', 'error');
      return;
    }

    const errors = {
      fullName: validateName(form.fullName, 'Full name'),
      email: validateEmail(form.email),
      phone: validatePhone(form.phone),
      organizationName: validateName(form.organizationName, 'Organization name'),
      organizationPhone: validatePhone(form.organizationPhone),
    };
    setFormErrors(errors);
    const firstError = Object.values(errors).find(Boolean);
    if (firstError) {
      showToast(firstError, 'error');
      return;
    }

    setSaving(true);
    try {
      const uploadImage = async (file, endpoint, filename) => {
        if (!file) return null;
        const data = new FormData();
        data.append('file', file, filename || file.name || 'profile-photo.jpg');
        const response = await API.post(endpoint, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (!response.data?.success || !response.data?.image_url) {
          throw new Error(response.data?.error || 'Image upload failed');
        }
        return response.data.image_url;
      };

      const logoUrl = await uploadImage(logoFile, '/api/upload/logo');
      const signatureUrl = await uploadImage(signatureFile, '/api/upload/signature');
      const profilePhotoUrl = await uploadImage(
        pendingProfilePhoto?.file,
        isOwner ? '/api/upload/tenant-profile-photo' : '/api/upload/profile-photo',
        pendingProfilePhoto?.fileName
      );

      // If the user removed an asset, send null so the backend clears it.
      const finalLogo = form.organizationLogo ? (logoUrl || form.organizationLogo.replace(BACKEND, '')) : null;
      const finalSignature = form.organizationSignature ? (signatureUrl || form.organizationSignature.replace(BACKEND, '')) : null;

      // Only send the role when it was actually changed.
      const roleChanged = normalizeRole(form.role) !== normalizeRole(savedForm?.role);

      const payload = {
        name: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        ...(roleChanged ? { role: normalizeRole(form.role) } : {}),
        ...(profilePhotoUrl ? { profile_photo: profilePhotoUrl } : {}),
        tenant: {
          tenant_name: form.organizationName.trim(),
          phone: form.organizationPhone.trim(),
          address: form.organizationAddress.trim(),
          logo_url: finalLogo,
          signature_url: finalSignature,
        },
      };

      const response = await API.put('/api/users/me', payload);
      const updatedUser = response.data?.user;
      const updatedTenant = response.data?.tenant;

      // A removed photo is deleted in its own endpoint once the profile update
      // has succeeded. A newly selected photo always wins over the removal flag.
      const photoRemoved = Boolean(removePhotoPending) && !profilePhotoUrl;
      if (photoRemoved) {
        try {
          await API.delete('/api/users/me/profile-photo');
        } catch (error) {
          // A 404 means the photo was already gone, so the end state is correct.
          if (error.response?.status !== 404) throw error;
        }
      }

      const deleteAsset = async (endpoint) => {
        try {
          await API.delete(endpoint);
        } catch (error) {
          // The PUT already sent null for this asset, so a failed cleanup call
          // should not make an otherwise successful save look broken.
          console.warn(`Could not clean up ${endpoint}:`, error);
        }
      };
      if (removeLogoPending && !logoUrl) await deleteAsset('/api/upload/logo');
      if (removeSignaturePending && !signatureUrl) await deleteAsset('/api/upload/signature');

      const savedUser = { ...(user || {}), ...(updatedUser || {}) };
      if (profilePhotoUrl) savedUser.profile_photo = profilePhotoUrl;
      if (photoRemoved) savedUser.profile_photo = null;
      if (updatedUser || profilePhotoUrl || photoRemoved) {
        localStorage.setItem('userinfo', JSON.stringify(savedUser));
      }

      // Fold the confirmed removals into the cached tenant so the old asset
      // does not come back on the next page load.
      const savedTenant = { ...(updatedTenant || {}) };
      const logoRemoved = removeLogoPending && !logoUrl;
      const signatureRemoved = removeSignaturePending && !signatureUrl;
      if (logoRemoved) savedTenant.logo_url = null;
      if (signatureRemoved) savedTenant.signature_url = null;
      if (updatedTenant || logoRemoved || signatureRemoved) {
        localStorage.setItem('tenantinfo', JSON.stringify(savedTenant));
      }

      if (profilePhotoUrl) {
        const savedPhotoUrl = makeUrl(profilePhotoUrl);
        const cacheBuster = savedPhotoUrl.includes('?') ? '&' : '?';
        const nextPhoto = `${savedPhotoUrl}${cacheBuster}v=${Date.now()}`;
        setProfilePhoto(nextPhoto);
        setSavedPhoto(nextPhoto);
      } else if (photoRemoved) {
        setProfilePhoto('');
        setSavedPhoto('');
      }
      window.dispatchEvent(new CustomEvent('profileUpdated', { detail: savedUser }));

      const nextForm = {
        ...form,
        fullName: updatedUser?.name ?? form.fullName,
        email: updatedUser?.email ?? form.email,
        phone: updatedUser?.phone ?? form.phone,
        role: updatedUser?.role ?? form.role,
        organizationName: updatedTenant?.tenant_name ?? updatedTenant?.name ?? form.organizationName,
        organizationPhone: updatedTenant?.phone ?? form.organizationPhone,
        organizationAddress: updatedTenant?.address ?? form.organizationAddress,
        organizationLogo: logoRemoved ? '' : makeUrl(savedTenant.logo_url ?? logoUrl ?? form.organizationLogo),
        organizationSignature: signatureRemoved ? '' : makeUrl(savedTenant.signature_url ?? signatureUrl ?? form.organizationSignature),
        organizationUpiQr: makeUrl(savedTenant.upi_qr_code ?? form.organizationUpiQr),
        lastLogin: updatedUser?.last_login ?? form.lastLogin,
        status: updatedUser?.status ?? form.status,
      };
      setForm(nextForm);
      setSavedForm(nextForm);
      setLogoFile(null);
      setSignatureFile(null);
      setPendingProfilePhoto(null);
      setRemovePhotoPending(false);
      setRemoveLogoPending(false);
      setRemoveSignaturePending(false);
      setFormErrors({});
      setUser(prev => ({
        ...prev,
        ...updatedUser,
        ...nextForm,
        ...(profilePhotoUrl ? { profile_photo: profilePhotoUrl } : {}),
        ...(photoRemoved ? { profile_photo: null } : {}),
        createdAt: prev?.createdAt,
      }));
      setEditing(false);
      showToast(
        photoRemoved
          ? 'Profile updated and photo removed'
          : 'Profile and organization details updated successfully',
        'success'
      );
    } catch (error) {
      console.error('Profile update failed:', error);
      showToast(error.response?.data?.error || error.response?.data?.message || error.message || 'Could not save your profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (savedForm) setForm(savedForm);
    setLogoFile(null);
    setSignatureFile(null);
    setPendingProfilePhoto(null);
    setRemovePhotoPending(false);
    setRemoveLogoPending(false);
    setRemoveSignaturePending(false);
    setProfilePhoto(savedPhoto);
    setFormErrors({});
    setEditing(false);
  };

  const pickImage = (label, setFile, key, clearRemoval) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast(`Please select an image file for the ${label}.`, 'error');
      e.target.value = '';
      return;
    }
    setFile(file);
    setForm(prev => ({ ...prev, [key]: URL.createObjectURL(file) }));
    if (clearRemoval) clearRemoval(false);
    e.target.value = '';
  };

  const clearAsset = (key, setFile) => {
    setFile(null);
    setForm(prev => ({ ...prev, [key]: '' }));
  };

  const confirmAssetRemoval = () => {
    if (confirmAsset === 'logo') {
      if (logoFile) {
        // Only an unsaved upload is being discarded, so restore the saved logo.
        setLogoFile(null);
        setForm(prev => ({ ...prev, organizationLogo: savedForm?.organizationLogo || '' }));
        setRemoveLogoPending(false);
      } else {
        clearAsset('organizationLogo', setLogoFile);
        setRemoveLogoPending(Boolean(savedForm?.organizationLogo));
      }
    }
    if (confirmAsset === 'signature') {
      if (signatureFile) {
        setSignatureFile(null);
        setForm(prev => ({ ...prev, organizationSignature: savedForm?.organizationSignature || '' }));
        setRemoveSignaturePending(false);
      } else {
        clearAsset('organizationSignature', setSignatureFile);
        setRemoveSignaturePending(Boolean(savedForm?.organizationSignature));
      }
    }
    if (confirmAsset === 'photo') {
      setPendingProfilePhoto(null);
      setProfilePhoto('');
      setRemovePhotoPending(true);
    }
    setConfirmAsset(null);
  };

  const closePasswordDialog = () => {
    if (passwordSaving) return;
    setPasswordOpen(false);
    setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    const { oldPassword, newPassword, confirmPassword } = passwordForm;
    if (!oldPassword) return setPasswordError('Current password is required');
    if (!newPassword) return setPasswordError('New password is required');
    if (newPassword !== confirmPassword) return setPasswordError('New passwords do not match');
    if (newPassword.length < 6) return setPasswordError('Password must be at least 6 characters');

    setPasswordSaving(true);
    setPasswordError('');
    try {
      await API.put('/api/users/me', {
        password: newPassword,
        old_password: oldPassword,
      });
      showToast('Password changed successfully', 'success');
      setPasswordOpen(false);
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to change password';
      setPasswordError(message);
    } finally {
      setPasswordSaving(false);
    }
  };

  const visibleLogoTile = editing || Boolean(form.organizationLogo);
  const visibleSignatureTile = editing || Boolean(form.organizationSignature);
  const visibleUpiTile = Boolean(form.organizationUpiQr);
  const visibleAssetCount = Number(visibleLogoTile) + Number(visibleSignatureTile) + Number(visibleUpiTile);
  const hasCurrentPhoto = Boolean(pendingProfilePhoto || (!removePhotoPending && (user?.profile_photo || profilePhoto)));
  const confirmAssetLabel = confirmAsset === 'photo'
    ? 'profile photo'
    : confirmAsset === 'logo'
      ? 'organization logo'
      : 'organization signature';

  return (
    <div className={`profile-wrapper${editing ? ' is-editing' : ''}`}>
      <header className="profile-top-header">
        <div className="profile-heading">
          <span className="profile-heading-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="8.4" r="3.5" />
              <path d="M5.2 19.8a6.8 6.8 0 0 1 13.6 0" />
            </svg>
          </span>
          <div className="profile-heading-copy">
            <h1>{editing ? 'Edit profile' : 'My profile'}</h1>
            <p>{editing ? 'Update your details, then save your changes.' : 'Manage your personal and organization information'}</p>
          </div>
        </div>
        <div className="profile-header-actions">
          {editing ? (
            <span className="editing-pill">
              <span className="editing-dot" aria-hidden="true" />
              Editing
            </span>
          ) : isOwner ? (
            <button type="button" className="edit-top-btn" onClick={startEditing} aria-label="Edit profile" title="Edit profile">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5 4 4M4 20l4.5-1 10.8-10.8a2.1 2.1 0 0 0-3-3L5.5 16 4 20Z" /></svg>
              <span className="edit-top-label">Edit profile</span>
            </button>
          ) : (
            <span className="readonly-pill">
              <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS.lock}</svg>
              Read only
            </span>
          )}
        </div>
      </header>

      {!isOwner && (
        <div className="profile-readonly-banner" role="note">
          <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS.lock}</svg>
          <div>
            <strong>Read-only access</strong>
            <span>Profile and organization settings can only be changed by the tenant owner.</span>
          </div>
        </div>
      )}

      <form className="profile-form" onSubmit={handleSave}>
        <div className="profile-layout">
          <aside className="profile-card profile-identity" aria-label="Profile summary">
            <div className="profile-avatar-wrap">
              <ProfilePhotoUpload
                currentPhoto={pendingProfilePhoto?.previewUrl || (removePhotoPending ? '' : user?.profile_photo || profilePhoto)}
                userName={form.fullName}
                userRole={user?.role || form.role}
                isEditing={editing}
                size={avatarSize}
                onPhotoFileReady={(file, fileName) => {
                  const previewUrl = URL.createObjectURL(file);
                  setPendingProfilePhoto({ file, fileName, previewUrl });
                  setProfilePhoto(previewUrl);
                  setRemovePhotoPending(false);
                }}
              />
            </div>
            {editing && (
              <div className="profile-photo-actions">
                <p className="profile-photo-hint">
                  {pendingProfilePhoto
                    ? 'New photo selected'
                    : removePhotoPending
                      ? 'Photo will be removed when you save'
                      : hasCurrentPhoto
                        ? 'Use the camera to change your photo'
                        : 'Use the camera to add a photo'}
                </p>
                {hasCurrentPhoto && !removePhotoPending && (
                  <button
                    type="button"
                    className="photo-remove-btn"
                    onClick={() => setConfirmAsset('photo')}
                    disabled={saving}
                  >
                    Remove photo
                  </button>
                )}
              </div>
            )}
            <h2 className="profile-name">{form.fullName || 'Name not provided'}</h2>
            <p className="sub-email">{form.email || 'Email not provided'}</p>
            <div className="identity-badges">
              <span className="role-badge">
                <svg viewBox="0 0 24 24" aria-hidden="true">{isOwner ? ICONS.crown : ICONS.shield}</svg>
                {roleLabel(form.role)}
              </span>
              <span className={`account-status${accountIsActive ? ' is-active' : ' is-inactive'}`}>
                <i aria-hidden="true" />
                {accountIsActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <dl className="identity-meta">
              <Detail label="Organization" value={form.organizationName} />
              <Detail label="Account created" value={createdLabel} fallback="Not available" />
            </dl>
            {!editing && isOwner && (
              <button type="button" className="password-btn" onClick={() => setPasswordOpen(true)}>
                <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS.key}</svg>
                Change password
              </button>
            )}
          </aside>

          <div className="profile-main">
            <section className="profile-card" aria-label="Account details">
              <div className="profile-section">
                <SectionTitle icon="person">Personal information</SectionTitle>
                <dl className="detail-grid">
                  <Field
                    id="profile-full-name"
                    label="Full name"
                    editing={editing}
                    required
                    autoComplete="name"
                    maxLength={150}
                    value={form.fullName}
                    onChange={handleNameChange('fullName', 'Full name')}
                    onKeyDown={preventDigits}
                    error={formErrors.fullName}
                  />
                  <Field
                    id="profile-role"
                    label="Role"
                    editing={false}
                    value={form.role}
                    display={roleLabel(form.role)}
                  />
                </dl>
              </div>

              <div className="profile-section">
                <SectionTitle icon="mail">Contact information</SectionTitle>
                <dl className="detail-grid">
                  <Field
                    id="profile-email"
                    label="Email address"
                    type="email"
                    editing={editing}
                    required
                    autoComplete="email"
                    maxLength={150}
                    value={form.email}
                    onChange={handleEmailChange}
                    error={formErrors.email}
                  />
                  {(editing || form.phone) && (
                    <Field
                      id="profile-phone"
                      label="Phone number"
                      type="tel"
                      editing={editing}
                      autoComplete="tel"
                      maxLength={10}
                      inputMode="numeric"
                      placeholder="10-digit phone number"
                      value={form.phone}
                      onChange={handlePhoneChange('phone')}
                      error={formErrors.phone}
                    />
                  )}
                  {!editing && lastLoginLabel && (
                    <Field id="profile-last-login" label="Last login" editing={false} value={lastLoginLabel} />
                  )}
                </dl>
              </div>
            </section>

            <section className="profile-card" aria-label="Organization">
              <div className="profile-section">
                <SectionTitle
                  icon="building"
                  aside={<span className="plan-badge">{form.planType || 'Basic'}</span>}
                >
                  Organization information
                </SectionTitle>
                <dl className="detail-grid">
                  <Field
                    id="profile-organization-name"
                    label="Organization name"
                    editing={editing}
                    required
                    maxLength={255}
                    value={form.organizationName}
                    onChange={handleNameChange('organizationName', 'Organization name')}
                    onKeyDown={preventDigits}
                    error={formErrors.organizationName}
                  />
                  {(editing || form.organizationPhone) && (
                    <Field
                      id="profile-organization-phone"
                      label="Organization phone"
                      type="tel"
                      editing={editing}
                      maxLength={10}
                      inputMode="numeric"
                      placeholder="10-digit phone number"
                      value={form.organizationPhone}
                      onChange={handlePhoneChange('organizationPhone')}
                      error={formErrors.organizationPhone}
                    />
                  )}
                  {(editing || form.organizationAddress) && (
                    <Field
                      id="profile-organization-address"
                      label="Organization address"
                      editing={editing}
                      multiline
                      className="wide"
                      value={form.organizationAddress}
                      onChange={setField('organizationAddress')}
                    />
                  )}
                  {!editing && form.ownerName && (
                    <Field id="profile-owner-name" label="Owner name" editing={false} value={form.ownerName} />
                  )}
                  {!editing && form.ownerEmail && (
                    <Field id="profile-owner-email" label="Owner email" editing={false} value={form.ownerEmail} />
                  )}
                  {!editing && form.organizationDomain && (
                    <Field id="profile-domain" label="Custom domain" editing={false} value={form.organizationDomain} />
                  )}
                </dl>

                {visibleAssetCount > 0 && (
                  <div className={`asset-grid${visibleAssetCount === 1 ? ' is-single' : ''}`}>
                    {visibleLogoTile && (
                      <AssetTile
                        id="organization-logo"
                        label="Organization logo"
                        src={form.organizationLogo}
                        emptyText="No logo uploaded"
                        editing={editing}
                        onSelect={pickImage('logo', setLogoFile, 'organizationLogo', setRemoveLogoPending)}
                        onRemove={() => setConfirmAsset('logo')}
                      />
                    )}
                    {visibleSignatureTile && (
                      <AssetTile
                        id="organization-signature"
                        label="Organization signature"
                        src={form.organizationSignature}
                        emptyText="No signature uploaded"
                        editing={editing}
                        onSelect={pickImage('signature', setSignatureFile, 'organizationSignature', setRemoveSignaturePending)}
                        onRemove={() => setConfirmAsset('signature')}
                      />
                    )}
                    {visibleUpiTile && (
                      <AssetTile
                        id="organization-upi-qr"
                        label="UPI QR code"
                        src={form.organizationUpiQr}
                        emptyText="No QR code uploaded"
                        editing={false}
                      />
                    )}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        {editing && (
          <div className="profile-actionbar" role="region" aria-label="Save or cancel your changes">
            <p>{hasErrors ? 'Please fix the highlighted fields before saving.' : 'Changes aren’t saved until you select Save changes.'}</p>
            <div className="profile-actionbar-buttons">
              <button type="button" className="btn ghost" onClick={cancelEdit} disabled={saving}>Cancel</button>
              <button type="submit" className="btn primary" disabled={saving || hasErrors}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        )}
      </form>

      {passwordOpen && (
        <div
          className="profile-dialog-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closePasswordDialog(); }}
        >
          <div className="profile-dialog" role="dialog" aria-modal="true" aria-labelledby="profile-password-title">
            <div className="profile-dialog-head">
              <DialogIcon type="lock" />
              <div>
                <h2 id="profile-password-title">Change password</h2>
                <p>Use at least 6 characters. You will need the new password next time you sign in.</p>
              </div>
              <button type="button" className="profile-dialog-close" onClick={closePasswordDialog} aria-label="Close" disabled={passwordSaving}>×</button>
            </div>
            <form onSubmit={handlePasswordSave}>
              <div className="profile-dialog-body">
                <div className="profile-dialog-field">
                  <label htmlFor="profile-old-password">Current password</label>
                  <input
                    id="profile-old-password"
                    className="detail-input"
                    type="password"
                    autoComplete="current-password"
                    value={passwordForm.oldPassword}
                    onChange={e => { setPasswordForm(prev => ({ ...prev, oldPassword: e.target.value })); setPasswordError(''); }}
                    required
                  />
                </div>
                <div className="profile-dialog-field">
                  <label htmlFor="profile-new-password">New password</label>
                  <input
                    id="profile-new-password"
                    className="detail-input"
                    type="password"
                    autoComplete="new-password"
                    minLength={6}
                    value={passwordForm.newPassword}
                    onChange={e => { setPasswordForm(prev => ({ ...prev, newPassword: e.target.value })); setPasswordError(''); }}
                    required
                  />
                </div>
                <div className="profile-dialog-field">
                  <label htmlFor="profile-confirm-password">Confirm new password</label>
                  <input
                    id="profile-confirm-password"
                    className="detail-input"
                    type="password"
                    autoComplete="new-password"
                    minLength={6}
                    value={passwordForm.confirmPassword}
                    onChange={e => { setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value })); setPasswordError(''); }}
                    required
                  />
                </div>
                {passwordError && <p className="profile-dialog-error" role="alert">{passwordError}</p>}
              </div>
              <div className="profile-dialog-actions">
                <button type="button" className="btn ghost" onClick={closePasswordDialog} disabled={passwordSaving}>Cancel</button>
                <button type="submit" className="btn primary" disabled={passwordSaving}>
                  {passwordSaving ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmAsset && (
        <div
          className="profile-dialog-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmAsset(null); }}
        >
          <div className="profile-dialog is-compact" role="dialog" aria-modal="true" aria-labelledby="profile-remove-title">
            <div className="profile-dialog-head">
              <DialogIcon type="danger" />
              <div>
                <h2 id="profile-remove-title">Remove {confirmAssetLabel}?</h2>
                <p>The change takes effect after you select Save changes.</p>
              </div>
            </div>
            <div className="profile-dialog-actions">
              <button type="button" className="btn ghost" onClick={() => setConfirmAsset(null)}>Cancel</button>
              <button type="button" className="btn danger" onClick={confirmAssetRemoval}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const Profile = ProfilePage;
export default ProfilePage;